import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import React, { useContext, useEffect } from "react";
import { blue, grey, red } from "@mui/material/colors";
import MedicationIcon from "@mui/icons-material/Medication";
import Divider from "@mui/material/Divider";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { NotificationType } from "../../constants/global";
import { useMutationDeleteCart } from "../../queries/product/hooks/useMutationDeleteCart";
import { CART } from "../../queries/product/productTypeDefs";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { UserContext } from "../../hooks/userContext";
import moment from "moment";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  formatPrice,
  getSealedDisplay,
  getPackTypeDisplay,
  getSealDetailsDisplay,
  formatProductOptionalPackageConditions,
  getProductOptionalPackageConditions,
  getOptionalPackageConditionTooltip,
} from "../../components/utils/utils";
import axios from "axios";

interface CheckoutItemProps {
  cartItem: any;
  cartGroupData: any;
  setCartGroupData: any;
  shippingRatesLoading: boolean;
}
export const CheckoutItem: React.FunctionComponent<CheckoutItemProps> = ({
  cartItem,
  cartGroupData,
  setCartGroupData,
  shippingRatesLoading
}) => {
  const isNonSealed = cartItem?.item?.packageCondition !== 1;
  const isPartialPackType = cartItem?.item?.type !== 1;
  const sealDetailText = cartItem?.item?.sealDetails
    ? getSealDetailsDisplay(cartItem.item.sealDetails)
    : null;
  const optionalPackageConditions = getProductOptionalPackageConditions(cartItem.item, {
    shortenOtherLabel: true,
  });
  const optionalPackageConditionsText = formatProductOptionalPackageConditions(cartItem.item, {
    shortenOtherLabel: true,
  });
  const optionalPackageConditionTooltip = getOptionalPackageConditionTooltip(cartItem.item, {
    shortenOtherLabel: true,
  });
  const alertBadgeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: red[400],
    color: '#fff',
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1,
    ml: 0.75,
    boxShadow: `0 0 0 1px ${red[400]}`
  };
  const checkoutFieldFontSize = '12px';
  const fieldLabelSx = {
    opacity: 0.5,
    mb: 1,
    fontSize: checkoutFieldFontSize,
  };
  const fieldValueSx = {
    fontSize: checkoutFieldFontSize,
  };
  const headingWithBadgeSx = {
    color: 'text.secondary',
    mb: 1,
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 20,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    fontSize: checkoutFieldFontSize,
  };

  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const [deleteCart, { loading }] = useMutationDeleteCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +id } },
          orderBy: { createdAt: 'asc' }
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);

  useEffect(() => {
    if (!cartItem) return;
    console.log('cartItem', cartItem);
    const ndc = cartItem.item.ndc;
    const formattedNdc = ndc.replace(/-/g, '');
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`;
    axios.get(imageUrlApi)
      .then((response) => {
        setImageUrl(response.data.imageUrl);
      })
      .catch((error) => {
        console.error("Error fetching drug image:", error);
      })

  }, [cartItem]);

  const deleteCartItem = (item: any) => {
    const newCartGroupData = [...cartGroupData];

    cartGroupData.forEach((cartGroupDataItem: any, index: number) => {
      if (cartGroupDataItem.groupItem.length === 1) {
        const newGroupItem = [...cartGroupDataItem.groupItem];
        const newCartGroupItemIndex = newGroupItem.findIndex((newGroupIndexItem: any) => newGroupIndexItem.id === item.id);

        if (newCartGroupItemIndex !== -1) {
          newCartGroupData.splice(index, 1);
        }
      } else {
        const newGroupItem = [...cartGroupDataItem.groupItem];
        const newCartGroupItemIndex = newGroupItem.findIndex((newGroupIndexItem: any) => newGroupIndexItem.id === item.id);
        if (newCartGroupItemIndex !== -1) {
          newGroupItem.splice(newCartGroupItemIndex, 1);
          newCartGroupData[index] = {
            ...newCartGroupData[index],
            groupItem: newGroupItem
          }
        }
      }
    });

    setCartGroupData(newCartGroupData);

    if (!loading) {
      deleteCart({ variables: { where: { id: item.id } } }).catch((error: any) => {
        addToast({ type: NotificationType.Error, title: error.message });
      });
    }
  }

  const checkoutColumnSx = {
    minWidth: 0,
    flexShrink: 0,
  };

  return (
    <>
      <Box sx={{ p: 2, backgroundColor: cartItem.item.isFrozen ? blue[50] : '' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: { md: 'nowrap' },
            gap: 2,
            alignItems: 'flex-start',
            overflowX: { md: 'auto' },
          }}
        >
          <Box sx={{ ...checkoutColumnSx, flex: '1.6 1 160px', minWidth: { md: 160 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {imageUrl ? <img src={imageUrl} alt="Drug" style={{ width: '40px', height: '40px', marginRight: '10px' }} /> : <MedicationIcon sx={{ color: grey[500], mr: 1 }} />}
              <Box sx={{ fontSize: checkoutFieldFontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {cartItem.item.drugName} {cartItem.item.isFrozen ? <span style={{ color: blue[500] }}>&nbsp;(Refrigerated)</span> : ''}
              </Box>
            </Box>
          </Box>
          <Box sx={{ ...checkoutColumnSx, flex: '1 1 100px', minWidth: { md: 100 } }}>
            <Box sx={fieldLabelSx}>NDC Number</Box>
            <Box sx={fieldValueSx}>{cartItem.item.ndc}</Box>
          </Box>
          <Box sx={{ ...checkoutColumnSx, flex: '0.8 1 72px', minWidth: { md: 72 } }}>
            <Box sx={fieldLabelSx}>Strength</Box>
            <Box sx={fieldValueSx}>{cartItem.item.strength}</Box>
          </Box>
          <Box sx={{ ...checkoutColumnSx, flex: '0.8 1 72px', minWidth: { md: 72 } }}>
            <Box sx={fieldLabelSx}>Exp Date</Box>
            <Box sx={fieldValueSx}>{moment(cartItem.item.expDate).format('MM/YYYY')}</Box>
          </Box>
          <Box sx={{ ...checkoutColumnSx, flex: '1.2 1 140px', minWidth: { md: 140 } }}>
            <Box sx={headingWithBadgeSx}>
              Seal Details
              {isNonSealed && (
                <Tooltip title="Non-Sealed product" arrow placement="top">
                  <Box component="span" sx={alertBadgeSx}>!</Box>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ ...fieldValueSx, lineHeight: 1.35 }}>
              {getSealedDisplay(cartItem.item.packageCondition, cartItem.item.type)}
            </Box>
            {sealDetailText && (
              <Box sx={{ ...fieldValueSx, color: 'text.secondary', lineHeight: 1.35 }}>
                {sealDetailText}
              </Box>
            )}
          </Box>
          {optionalPackageConditions.length > 0 && (
            <Box sx={{ ...checkoutColumnSx, flex: '1.4 1 168px', minWidth: { md: 168 } }}>
              <Box sx={fieldLabelSx}>Package Condition</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', minWidth: 0 }}>
                <Box
                  sx={{
                    ...fieldValueSx,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {optionalPackageConditionsText}
                </Box>
                {optionalPackageConditionTooltip && (
                  <Tooltip
                    title={<Box sx={{ whiteSpace: 'pre-line' }}>{optionalPackageConditionTooltip}</Box>}
                    arrow
                    placement="top"
                  >
                    <WarningAmberOutlinedIcon
                      sx={{ fontSize: 16, color: 'warning.main', cursor: 'help', flexShrink: 0 }}
                      aria-label="Optional package condition details"
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}
          <Box sx={{ ...checkoutColumnSx, flex: '0.9 1 96px', minWidth: { md: 96 } }}>
            <Box sx={headingWithBadgeSx}>
              Pack Type
              {isPartialPackType && (
                <Tooltip title="Partial Prodcut" arrow placement="top">
                  <Box component="span" sx={alertBadgeSx}>!</Box>
                </Tooltip>
              )}
            </Box>
            <Box sx={fieldValueSx}>
              {getPackTypeDisplay(cartItem.item.type, cartItem.item.packaging)}
            </Box>
          </Box>
          <Box sx={{ ...checkoutColumnSx, flex: '0.6 1 64px', minWidth: { md: 64 } }}>
            <Box sx={fieldLabelSx}>Quantity</Box>
            <Box sx={fieldValueSx}>{cartItem.quantity}</Box>
          </Box>
          <Box sx={{ ...checkoutColumnSx, flex: '0.7 1 72px', minWidth: { md: 72 } }}>
            <Box sx={fieldLabelSx}>Per Unit</Box>
            <Box sx={fieldValueSx}>${formatPrice(cartItem.item.unitPrice)}</Box>
          </Box>
          <Box sx={{ ...checkoutColumnSx, flex: '0.8 1 88px', minWidth: { md: 88 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={fieldLabelSx}>Price</Box>
                <Box sx={fieldValueSx}>${formatPrice(cartItem.item.price)}</Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <DeleteOutlineOutlinedIcon
                  sx={{ p: 0, cursor: shippingRatesLoading ? 'default' : 'pointer', color: shippingRatesLoading ? grey[500] : red[500] }}
                  onClick={() => shippingRatesLoading ? undefined : deleteCartItem(cartItem)}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider />
    </>
  )
}